import { useTranslation } from "react-i18next";
import { Form, Grid, GridColumn, Icon } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { PIN_TYPES } from "models";
import { NoteAttachments } from "modules/AppointmentDetails/components/Interventions/components";
import "modules/AppointmentDetails/components/Interventions/components/PinCategory/components/PinCategoryForm/PinCategoryForm.scss";

import type { DropdownItemProps } from "components";
import type { Intervention, PIN_ORDER_STATUSES, PIN_STATUSES, WARRANTY_TYPES } from "models";
import type { NoteAttachment } from "modules/AppointmentDetails/components/Interventions/components";
import type { Dispatch, SetStateAction } from "react";

type PinCategoryFormProps = {
  selectedPinTypeOption: DropdownItemProps<PIN_TYPES>;
  intervention: Intervention;
  pinStatusOptions: DropdownItemProps<PIN_STATUSES>[];
  pinOrderOptions: DropdownItemProps<PIN_ORDER_STATUSES>[];
  pinWarrantyOptions: DropdownItemProps<WARRANTY_TYPES>[];
  attachments: NoteAttachment[];
  setAttachments: Dispatch<SetStateAction<NoteAttachment[]>>;
};

export type PinCategoryFormType = {
  supportNr: string;
  claimNr: string;
  refNr: string;
  keepParts: boolean;
  orderStatus: number | null;
  pinWarrantyType: WARRANTY_TYPES | null;
  pinStatus: PIN_STATUSES | null;
};

const FormHelper = createFormHelper<PinCategoryFormType>();

export const PinCategoryForm = ({
  intervention,
  selectedPinTypeOption,
  attachments,
  pinOrderOptions,
  pinStatusOptions,
  pinWarrantyOptions,
  setAttachments
}: PinCategoryFormProps) => {
  const { t } = useTranslation();

  return (
    <div className="PinCategoryForm">
      <Grid columns={16}>
        <GridColumn width={5}>
          <Form.Field>
            <label>{t("v8_support", "Support")} #</label>
            <FormHelper.Field.Input name="supportNr" inputProps={{ fluid: true, disabled: true }} />
          </Form.Field>
        </GridColumn>

        <GridColumn width={5}>
          <Form.Field>
            <label>{t("v8_claim", "Claim")} #</label>
            <FormHelper.Field.Input name="claimNr" inputProps={{ fluid: true, disabled: true }} />
          </Form.Field>
        </GridColumn>

        <GridColumn width={4}>
          <Form.Field>
            <label>{t("v8_ref", "Ref")} #</label>
            <FormHelper.Field.Input name="refNr" inputProps={{ fluid: true, disabled: true }} />
          </Form.Field>
        </GridColumn>
        <GridColumn width={2} verticalAlign="bottom">
          <Form.Field>
            <div className="checkbox-container">
              <Icon className="setting" color="grey" />
              <span>{t("v8_keep_parts", "Keep Parts")}</span>
              <FormHelper.Field.Checkbox
                name="keepParts"
                formFieldProps={{ className: "checkbox-form-field", disabled: true }}
                checkboxProps={{ className: "checkbox-box" }}
              />
            </div>
          </Form.Field>
        </GridColumn>
      </Grid>

      <Grid columns={3}>
        <GridColumn>
          <Form.Field>
            <label>{t("v8_order_status", "Order status")}</label>
            <FormHelper.Field.Dropdown
              name="orderStatus"
              options={pinOrderOptions}
              dropdownProps={{
                disabled: true,
                fluid: true,
                placeholder: t("v8_select_order_status", "Select order status")
              }}
            />
          </Form.Field>
        </GridColumn>

        {selectedPinTypeOption.value === PIN_TYPES.Warranty && (
          <GridColumn>
            <Form.Field>
              <label>{t("v8_warranty_type", "Warranty type")}</label>
              <FormHelper.Field.Dropdown
                name="pinWarrantyType"
                options={pinWarrantyOptions}
                dropdownProps={{
                  disabled: true,
                  fluid: true,
                  placeholder: t("v8_select_type", "Select type")
                }}
              />
            </Form.Field>
          </GridColumn>
        )}
        {selectedPinTypeOption.value !== PIN_TYPES.Remarks && (
          <GridColumn>
            <Form.Field>
              <label>{t("v8_pin_status", "Pin Status")}</label>
              <FormHelper.Field.Dropdown
                name="pinStatus"
                options={pinStatusOptions}
                dropdownProps={{
                  disabled: true,
                  fluid: true,
                  placeholder: t("v8_select_status", "Select status")
                }}
              />
            </Form.Field>
          </GridColumn>
        )}
      </Grid>

      <Grid>
        <GridColumn>
          <NoteAttachments appointmentId={intervention.appointment_id} attachments={attachments} setAttachments={setAttachments} readOnly />
        </GridColumn>
      </Grid>
    </div>
  );
};
